import React, { useState, useEffect, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from '../components/Layout'
import { AuthContext } from '../App'
import axios from 'axios'
import { toast } from 'sonner'
import { Download, Eye, Calendar, Package, Trash2, Grid, List } from 'lucide-react'
import { Button } from '../components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog'
import { Checkbox } from '../components/ui/checkbox'
import AdvancedImageViewer from '../components/AdvancedImageViewer'
import { format } from 'date-fns'

const AllMedicineSets = () => {
  const { API } = useContext(AuthContext)
  const navigate = useNavigate()
  const [medicineSets, setMedicineSets] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedSet, setSelectedSet] = useState(null)
  const [showImageDialog, setShowImageDialog] = useState(false)
  const [viewMode, setViewMode] = useState('cards') // 'cards' or 'list'
  const [deletingId, setDeletingId] = useState(null)
  const [selectedSets, setSelectedSets] = useState([])
  const [exportingBulk, setExportingBulk] = useState(false)

  useEffect(() => {
    fetchMedicineSets()
  }, [])

  const fetchMedicineSets = async () => {
    try {
      const response = await axios.get(`${API}/medicine-sets?pushed=false`)
      setMedicineSets(response.data)
    } catch (error) {
      console.error('Failed to fetch medicine sets:', error)
      toast.error('Failed to load medicine sets')
    } finally {
      setLoading(false)
    }
  }

  const handleExport = async (setId, setName) => {
    try {
      toast.info('Generating Excel file...')
      const response = await axios.get(`${API}/medicine-sets/${setId}/export`, {
        responseType: 'blob'
      })
      
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${setName}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      
      toast.success('Excel file downloaded successfully!')
    } catch (error) {
      console.error('Export error:', error)
      toast.error('Failed to export medicine set')
    }
  }

  const handleViewImage = (set) => {
    setSelectedSet(set)
    setShowImageDialog(true)
  }

  const handleDelete = async (setId, setName) => {
    if (!window.confirm(`Are you sure you want to delete "${setName}"?`)) {
      return
    }

    setDeletingId(setId)
    try {
      await axios.delete(`${API}/medicine-sets/${setId}`)
      toast.success('Medicine set deleted successfully!')
      setMedicineSets(medicineSets.filter(s => s.id !== setId))
      setSelectedSets(selectedSets.filter(id => id !== setId))
    } catch (error) {
      console.error('Delete error:', error)
      toast.error('Failed to delete medicine set')
    } finally {
      setDeletingId(null)
    }
  }

  const handleBulkExport = async () => {
    if (selectedSets.length === 0) {
      toast.error('Please select at least one medicine set')
      return
    }

    setExportingBulk(true)
    try {
      toast.info('Generating combined Excel file...')
      const response = await axios.post(`${API}/medicine-sets/bulk-export`, selectedSets, {
        responseType: 'blob'
      })
      
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `Combined_Medicine_Sets_${Date.now()}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      
      toast.success('Combined Excel file downloaded successfully!')
      // Refresh to show updated pushed status
      fetchMedicineSets()
      setSelectedSets([])
    } catch (error) {
      console.error('Bulk export error:', error)
      toast.error('Failed to export selected medicine sets')
    } finally {
      setExportingBulk(false)
    }
  }

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedSets(medicineSets.map(s => s.id))
    } else {
      setSelectedSets([])
    }
  }

  const handleSelectSet = (setId, checked) => {
    if (checked) {
      setSelectedSets([...selectedSets, setId])
    } else {
      setSelectedSets(selectedSets.filter(id => id !== setId))
    }
  }

  if (loading) {
    return (
      <Layout title="All Medicine Sets">
        <div className="card text-center py-12">
          <p className="text-gray-600">Loading medicine sets...</p>
        </div>
      </Layout>
    )
  }

  if (medicineSets.length === 0) {
    return (
      <Layout title="All Medicine Sets">
        <div className="card text-center py-12" data-testid="empty-state">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Medicine Sets Yet</h3>
          <p className="text-gray-600 mb-4">Upload your first delivery challan to get started</p>
          <Button onClick={() => window.location.href = '/upload'} data-testid="upload-first-button">
            Upload Now
          </Button>
        </div>
      </Layout>
    )
  }

  return (
    <Layout title="All Medicine Sets">
      <div className="flex justify-between items-center mb-4">
        <div className="flex gap-3 items-center">
          {selectedSets.length > 0 && (
            <>
              <Button
                onClick={handleBulkExport}
                disabled={exportingBulk}
                data-testid="bulk-export-button"
                className="bg-teal-700"
              >
                <Download className="w-4 h-4 mr-2" />
                {exportingBulk ? 'Exporting...' : `Push Selected Data to eHCMS (${selectedSets.length})`}
              </Button>
              <Button
                variant="outline"
                onClick={() => setSelectedSets([])}
                data-testid="clear-selection"
              >
                Clear Selection
              </Button>
            </>
          )}
        </div>
        <div className="flex gap-2 bg-white rounded-lg p-1 shadow">
          <button
            onClick={() => setViewMode('cards')}
            className={`px-4 py-2 rounded ${viewMode === 'cards' ? 'bg-teal-700 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
            data-testid="view-cards"
          >
            <Grid className="w-4 h-4 inline mr-2" />
            Cards
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`px-4 py-2 rounded ${viewMode === 'list' ? 'bg-teal-700 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
            data-testid="view-list"
          >
            <List className="w-4 h-4 inline mr-2" />
            List
          </button>
        </div>
      </div>

      {viewMode === 'cards' ? (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {medicineSets.map((set) => (
          <div key={set.id} className="card text-sm" data-testid={`medicine-set-${set.id}`}>
            <div className="flex items-start gap-3 mb-3">
              <Checkbox
                checked={selectedSets.includes(set.id)}
                onCheckedChange={(checked) => handleSelectSet(set.id, checked)}
                data-testid={`checkbox-${set.id}`}
              />
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900 mb-1" data-testid={`set-name-${set.id}`}>{set.set_name}</h3>
                <div className="flex gap-4 text-xs text-gray-600 mb-2">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    <span>{set.medicines.length} medicines</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{format(new Date(set.created_at), 'MMM dd, yyyy')}</span>
                  </div>
                </div>
                <div className="flex gap-1 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewImage(set)}
                    data-testid={`view-image-${set.id}`}
                    className="text-xs px-2 py-1 h-7"
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    View
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleExport(set.id, set.set_name)}
                    data-testid={`export-${set.id}`}
                    className="text-xs px-2 py-1 h-7"
                  >
                    <Download className="w-3 h-3 mr-1" />
                    Push
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(set.id, set.set_name)}
                    disabled={deletingId === set.id}
                    data-testid={`delete-${set.id}`}
                    className="text-xs px-2 py-1 h-7"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    {deletingId === set.id ? 'Del...' : 'Delete'}
                  </Button>
                </div>
              </div>
            </div>

            <div className="mt-3 pt-3 border-t">
              <h4 className="font-semibold text-gray-700 text-xs mb-2">Medicines:</h4>
              <div className="grid grid-cols-1 gap-2">
                {set.medicines.slice(0, 3).map((medicine, idx) => (
                  <div key={idx} className="bg-gray-50 rounded p-2 text-xs" data-testid={`medicine-${set.id}-${idx}`}>
                    <p className="font-semibold text-gray-900">{medicine.name}</p>
                    <p className="text-xs text-gray-600">{medicine.form} {medicine.strength && `• ${medicine.strength}`}</p>
                  </div>
                ))}
                {set.medicines.length > 3 && (
                  <p className="text-xs text-gray-500 mt-1">+{set.medicines.length - 3} more medicines</p>
                )}
              </div>
            </div>

            <div className="mt-3 pt-3 border-t flex justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/verify', { state: { medicines: set.medicines, imageBase64: set.image_base64, setName: set.set_name, setId: set.id, isEditing: true } })}
                data-testid={`review-edit-${set.id}`}
                className="text-xs px-3 py-1 h-7 flex-1"
              >
                <Eye className="w-3 h-3 mr-1" />
                Review/Edit
              </Button>
            </div>
          </div>
        ))}
      </div>
      ) : (
        <div className="card">
          <div className="table-container">
            <table data-testid="medicine-sets-table">
              <thead>
                <tr>
                  <th>Set Name</th>
                  <th>Medicines</th>
                  <th>Created Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {medicineSets.map((set) => (
                  <tr key={set.id}>
                    <td className="font-semibold" data-testid={`list-set-name-${set.id}`}>{set.set_name}</td>
                    <td>{set.medicines.length}</td>
                    <td>{format(new Date(set.created_at), 'MMM dd, yyyy')}</td>
                    <td>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewImage(set)}
                          data-testid={`list-view-image-${set.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleExport(set.id, set.set_name)}
                          data-testid={`list-export-${set.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(set.id, set.set_name)}
                          disabled={deletingId === set.id}
                          data-testid={`list-delete-${set.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showImageDialog && selectedSet?.image_base64 && (
        <AdvancedImageViewer
          imageBase64={selectedSet.image_base64}
          onClose={() => setShowImageDialog(false)}
          title={`${selectedSet.set_name} - Original Image`}
        />
      )}
    </Layout>
  )
}

export default AllMedicineSets